import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { formatMoney, formatNumber } from '../../Shared/Formatters';
import { useApexTheme } from './useApexTheme';

/**
 * Area/line chart for the timeseries intent. Driven by real resolver data so
 * the curve, tooltips, and peak/low markers match the narrative bullets.
 */
export default function LineAreaBlock({ title, data, valueFormat, markers, dir = 'ltr' }) {
    const palette = useApexTheme();
    const items = Array.isArray(data) ? data : [];
    const isMoney = valueFormat !== 'integer';
    const fmt = (val) => (isMoney ? formatMoney(val) : formatNumber(val, 0));

    const series = useMemo(
        () => [{ name: '', data: items.map(d => ({ x: d.x, y: Number(d.y) || 0 })) }],
        [items],
    );

    const annotationPoints = useMemo(() => {
        if (!markers) return [];
        const pts = [];
        const color = palette[0] || '#06B6D4';
        if (markers.peak) {
            pts.push({
                x: markers.peak.x, y: Number(markers.peak.y) || 0,
                marker: { size: 5, fillColor: '#10B981', strokeColor: '#fff', strokeWidth: 2 },
                label: {
                    text: markers.peak.label, borderColor: '#10B981',
                    style: { fontSize: '10px', color: '#fff', background: '#10B981' },
                    offsetY: -2,
                },
            });
        }
        if (markers.trough && markers.trough.x !== (markers.peak && markers.peak.x)) {
            pts.push({
                x: markers.trough.x, y: Number(markers.trough.y) || 0,
                marker: { size: 5, fillColor: '#EF4444', strokeColor: '#fff', strokeWidth: 2 },
                label: {
                    text: markers.trough.label, borderColor: '#EF4444',
                    style: { fontSize: '10px', color: '#fff', background: '#EF4444' },
                    offsetY: 16,
                },
            });
        }
        return pts;
    }, [markers, palette]);

    if (!items.length) return null;

    const options = {
        chart: {
            type: 'area',
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui',
            background: 'transparent',
            animations: { enabled: true, speed: 400 },
            sparkline: { enabled: false },
            zoom: { enabled: false },
            rtl: dir === 'rtl',
        },
        colors: palette,
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.02, stops: [0, 100] },
        },
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 4 } },
        xaxis: {
            type: 'category',
            labels: {
                style: { fontSize: '10px', colors: 'var(--v3-text-secondary)' },
                rotate: 0,
                hideOverlappingLabels: true,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: {
            labels: {
                style: { fontSize: '10px', colors: 'var(--v3-text-secondary)' },
                formatter: (val) => fmt(val),
            },
        },
        grid: {
            borderColor: 'var(--v3-border, rgba(148,163,184,0.15))',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            padding: { left: 4, right: 8, top: 4, bottom: 0 },
        },
        annotations: { points: annotationPoints },
        tooltip: {
            theme: 'dark',
            x: { show: true },
            y: { formatter: (val) => fmt(val) },
        },
    };

    return (
        <div className="chat-block">
            {title && <div className="chat-block-title">{title}</div>}
            <Chart type="area" options={options} series={series} height={180} />
        </div>
    );
}
